import type {ApolloError} from '@apollo/client'
import React from 'react'
import type {BO_meQuery_} from '../../components/stack/auth.generated'

/** Listed as a post-logout redirect of the Keycloak client: change both together. */
export const SIGNED_OUT_PATH = '/auth/signed_out'

/**
 * The staff signs in through Google, straight from the back-office: Keycloak's
 * own identity-first page is skipped. The API relays the hint only where it is
 * listed for that environment; locally there is no such provider, and Keycloak
 * shows its form.
 */
export const STAFF_IDP_HINT = 'google'

/**
 * Reachable without a session, so the automatic sign-in of _app never fires on
 * them. `/auth/signed_out` and `/logout` would see it undo the sign-out the
 * user just asked for; the other two exist because no session could be
 * established, and signing in again is what sent the user there.
 */
export const PUBLIC_PATHS = ['/auth/error', '/auth/not_verified', '/logout', SIGNED_OUT_PATH]

/** The reasons a login comes back with, from the API (`login_*`) or from the loop guard (`session_not_kept`). */
export const AUTH_ERRORS = ['login_refused', 'login_failed', 'session_not_kept'] as const
export type AuthError = (typeof AUTH_ERRORS)[number]

export const isAuthError = (value: unknown): value is AuthError => AUTH_ERRORS.includes(value as AuthError)

export type AuthSession = {
  me: BO_meQuery_['me'] | undefined
  isAuthenticated: boolean
  isLoading: boolean
  /**
   * A session this tab held and cannot use any more: expired, or signed out
   * from a sibling tab. Distinct from never having had one: this state must not
   * be answered with an automatic sign-in, which the identity provider's own
   * session would complete without asking anything.
   */
  isSessionLost: boolean
  /** The identity could not be read for another reason than a missing session (API unreachable, 5xx). */
  identityError: ApolloError | undefined
  /** returnTo is an in-app path (router.asPath), never an absolute URL. */
  signIn: (options?: {returnTo?: string}) => void
  /**
   * Ends the session at the API, then at Keycloak. Resolves `true` when the
   * page is leaving for Keycloak, `false` when the sign-out could not be done
   * (said to the user): the caller then stays where it is.
   */
  signOut: () => Promise<boolean>
}

// No default value: a consumer mounted outside the provider must fail loudly
// rather than sit on an inert session that never signs in.
export const AuthSessionContext = React.createContext<AuthSession | undefined>(undefined)

export const useAuthSession = (): AuthSession => {
  const session = React.useContext(AuthSessionContext)
  if (!session) {
    throw new Error('useAuthSession must be used within an AuthSessionProvider')
  }

  return session
}

/**
 * The API answers a missing session with the scopes' refusal, never a 401, and
 * says why in `extensions.reason`: `UNAUTHENTICATED` when there is no usable
 * user (no session, or a blocked account), `FORBIDDEN` when a right is missing.
 * Only the first is the session's business: a missing permission is the page's.
 */
export const refusesSession = (errors: readonly {extensions?: Record<string, unknown>}[] | undefined) =>
  Boolean(errors?.some(error => error.extensions?.reason === 'UNAUTHENTICATED'))

const sessionRefusedListeners = new Set<() => void>()

export const notifySessionRefused = () => {
  for (const listener of sessionRefusedListeners) {
    listener()
  }
}

export const onSessionRefused = (listener: () => void) => {
  sessionRefusedListeners.add(listener)
  return () => {
    sessionRefusedListeners.delete(listener)
  }
}

/**
 * Set on the page a login returns to. Coming back with it and still no session
 * means the browser did not keep the cookie (the API off this site, an origin
 * it does not accept): another login would come back the same way, which
 * Keycloak's own session completes in a blink, for ever. Once the session is
 * found, it leaves the address.
 */
export const SIGNED_IN_MARKER = '__signed_in'

/**
 * The page a login comes back to: an in-app address, marked. Anything that
 * resolves to another origin (`//evil.example`, `/\evil.example`) or to a
 * public page (landing on `/logout` would sign the user out again) falls back
 * to the root.
 */
export const signInReturnUrl = (returnTo: string | undefined, origin: string): URL => {
  const candidate = returnTo?.startsWith('/') ? new URL(returnTo, origin) : undefined
  const url =
    candidate && candidate.origin === origin && !PUBLIC_PATHS.includes(candidate.pathname)
      ? candidate
      : new URL('/', origin)

  url.searchParams.set(SIGNED_IN_MARKER, '1')
  return url
}

export const withoutSignInMarker = (asPath: string): string | undefined => {
  // Any base will do: only the path, the query and the hash are read back.
  const url = new URL(asPath, 'http://localhost')

  if (!url.searchParams.has(SIGNED_IN_MARKER)) {
    return undefined
  }

  url.searchParams.delete(SIGNED_IN_MARKER)
  return `${url.pathname}${url.search}${url.hash}`
}
